import { previewLanguage } from "@/lib/previewLanguage";

const SUPPORTED = new Set(["pt-BR", "en", "es-419", "fr-CA"]);

export function normalizeLocale(locale) {
  if (SUPPORTED.has(locale)) return locale;
  if (typeof locale === "string") {
    const lower = locale.toLowerCase();
    if (lower.startsWith("pt")) return "pt-BR";
    if (lower.startsWith("es")) return "es-419";
    if (lower.startsWith("fr")) return "fr-CA";
  }
  return "en";
}

export function rememberLocale(locale) {
  // Not from an Admin → Responsive Preview window: it would change this device's language.
  if (typeof window === "undefined" || previewLanguage()) return;
  try {
    window.localStorage.setItem("mec_locale", normalizeLocale(locale));
  } catch {
    // Storage may be unavailable in privacy-focused browser modes.
  }
}

export function getRememberedLocale(fallback = "en") {
  const preview = previewLanguage();
  if (preview) return preview;
  if (typeof window !== "undefined") {
    try {
      const stored = window.localStorage.getItem("mec_locale");
      if (stored) return normalizeLocale(stored);
    } catch {
      // Ignore storage failures.
    }
    if (window.navigator?.language) return normalizeLocale(window.navigator.language);
  }
  return normalizeLocale(fallback);
}

export const authMessages = {
  "pt-BR": {
    genericError: "Algo deu errado. Tente novamente.",
    sending: "Enviando…",
    processing: "Processando…",
    showPassword: "Mostrar senha",
    hidePassword: "Ocultar senha",
    forgotConfirmation: "Se existir uma conta de e-mail e senha para este endereço, enviamos um link de redefinição. Verifique sua caixa de entrada e a pasta de spam.",
    resetTitle: "Redefinir senha",
    resetSubtitle: "Escolha uma nova senha para sua conta.",
    tokenLabel: "Token",
    newPassword: "Nova senha",
    confirmNewPassword: "Confirmar nova senha",
    resetSubmit: "Redefinir senha",
    resetSuccess: "Senha redefinida. Entre com a nova senha.",
    passwordTooShort: "A senha precisa ter ao menos 8 caracteres.",
    passwordMismatch: "As senhas não conferem.",
    changeTitle: "Altere sua senha",
    changeSubtitle: "Por segurança, defina uma nova senha antes de continuar.",
    currentPassword: "Senha atual",
    saveNewPassword: "Salvar nova senha",
    logoutWithoutChanging: "Sair sem alterar",
    changeSuccess: "Senha alterada.",
    accountCreated: "Conta criada!",
    incorrectCredentials: "E-mail ou senha incorretos.",
    tooManyAttempts: "Muitas tentativas de login. Aguarde alguns minutos e tente novamente.",
    accountDisabled: "Esta conta está desativada.",
    activeLoginExists: "Esta conta já está conectada em outro navegador ou dispositivo. Se essa sessão não estiver mais em uso, clique em Entrar novamente para encerrá-la e continuar aqui.",
    authRequired: "Sua sessão expirou. Entre novamente.",
    adminOnly: "Acesso restrito ao administrador.",
    accountExists: "Já existe uma conta com este e-mail.",
    privacyRequired: "Você precisa aceitar a Política de Privacidade para continuar.",
    ageRequired: "Você precisa confirmar que atingiu a maioridade legal para continuar.",
    policyVersion: "A versão da Política de Privacidade não foi reconhecida.",
    policyLanguage: "O idioma da Política de Privacidade não é suportado.",
    countryUnsupported: "País de residência não suportado durante o Beta.",
    firstNameRequired: "Informe seu primeiro nome.",
    googleNoPassword: "Contas Google não usam senha do aplicativo.",
    currentPasswordIncorrect: "A senha atual está incorreta.",
    tokenInvalid: "O link de redefinição é inválido ou já foi utilizado.",
    tokenExpired: "O link de redefinição expirou. Solicite um novo.",
    googleUnavailable: "O login com Google está indisponível no momento.",
    invalidEmail: "Informe um e-mail válido.",
  },
  en: {
    genericError: "Something went wrong. Please try again.",
    sending: "Sending…",
    processing: "Processing…",
    showPassword: "Show password",
    hidePassword: "Hide password",
    forgotConfirmation: "If an email/password account exists for this address, we sent a password reset link. Check your inbox and spam folder.",
    resetTitle: "Reset password",
    resetSubtitle: "Choose a new password for your account.",
    tokenLabel: "Token",
    newPassword: "New password",
    confirmNewPassword: "Confirm new password",
    resetSubmit: "Reset password",
    resetSuccess: "Password reset. Sign in with your new password.",
    passwordTooShort: "The password must be at least 8 characters long.",
    passwordMismatch: "The passwords do not match.",
    changeTitle: "Change your password",
    changeSubtitle: "For security, set a new password before continuing.",
    currentPassword: "Current password",
    saveNewPassword: "Save new password",
    logoutWithoutChanging: "Sign out without changing",
    changeSuccess: "Password changed.",
    accountCreated: "Account created!",
    incorrectCredentials: "Incorrect email or password.",
    tooManyAttempts: "Too many login attempts. Please wait a few minutes and try again.",
    accountDisabled: "This account is disabled.",
    activeLoginExists: "This account is already signed in on another browser or device. If that session is no longer in use, click Sign in again to sign it out and continue here.",
    authRequired: "Your session has expired. Please sign in again.",
    adminOnly: "Administrator access only.",
    accountExists: "An account already exists with this email.",
    privacyRequired: "You must accept the Privacy Policy to continue.",
    ageRequired: "You must confirm that you have reached the legal age of majority to continue.",
    policyVersion: "The Privacy Policy version was not recognized.",
    policyLanguage: "The Privacy Policy language is not supported.",
    countryUnsupported: "Country of residence is not supported during the Beta.",
    firstNameRequired: "Enter your first name.",
    googleNoPassword: "Google accounts do not use an app password.",
    currentPasswordIncorrect: "The current password is incorrect.",
    tokenInvalid: "This password-reset link is invalid or has already been used.",
    tokenExpired: "This password-reset link has expired. Request a new one.",
    googleUnavailable: "Google sign-in is unavailable right now.",
    invalidEmail: "Enter a valid email address.",
  },
  "es-419": {
    genericError: "Algo salió mal. Inténtelo de nuevo.",
    sending: "Enviando…",
    processing: "Procesando…",
    showPassword: "Mostrar contraseña",
    hidePassword: "Ocultar contraseña",
    forgotConfirmation: "Si existe una cuenta de correo y contraseña para esta dirección, enviamos un enlace para restablecer la contraseña. Revise su bandeja de entrada y la carpeta de spam.",
    resetTitle: "Restablecer contraseña",
    resetSubtitle: "Elija una nueva contraseña para su cuenta.",
    tokenLabel: "Token",
    newPassword: "Nueva contraseña",
    confirmNewPassword: "Confirmar nueva contraseña",
    resetSubmit: "Restablecer contraseña",
    resetSuccess: "Contraseña restablecida. Inicie sesión con su nueva contraseña.",
    passwordTooShort: "La contraseña debe tener al menos 8 caracteres.",
    passwordMismatch: "Las contraseñas no coinciden.",
    changeTitle: "Cambie su contraseña",
    changeSubtitle: "Por seguridad, establezca una nueva contraseña antes de continuar.",
    currentPassword: "Contraseña actual",
    saveNewPassword: "Guardar nueva contraseña",
    logoutWithoutChanging: "Salir sin cambiarla",
    changeSuccess: "Contraseña cambiada.",
    accountCreated: "¡Cuenta creada!",
    incorrectCredentials: "Correo electrónico o contraseña incorrectos.",
    tooManyAttempts: "Demasiados intentos de inicio de sesión. Espere unos minutos e inténtelo de nuevo.",
    accountDisabled: "Esta cuenta está desactivada.",
    activeLoginExists: "Esta cuenta ya tiene una sesión iniciada en otro navegador o dispositivo. Si esa sesión ya no está en uso, haga clic en Iniciar sesión nuevamente para cerrarla y continuar aquí.",
    authRequired: "Su sesión ha caducado. Inicie sesión de nuevo.",
    adminOnly: "Acceso restringido al administrador.",
    accountExists: "Ya existe una cuenta con este correo electrónico.",
    privacyRequired: "Debe aceptar la Política de Privacidad para continuar.",
    ageRequired: "Debe confirmar que ha alcanzado la mayoría de edad legal para continuar.",
    policyVersion: "No se reconoció la versión de la Política de Privacidad.",
    policyLanguage: "El idioma de la Política de Privacidad no es compatible.",
    countryUnsupported: "El país de residencia no es compatible durante la Beta.",
    firstNameRequired: "Ingrese su nombre.",
    googleNoPassword: "Las cuentas de Google no usan una contraseña de la aplicación.",
    currentPasswordIncorrect: "La contraseña actual es incorrecta.",
    tokenInvalid: "Este enlace para restablecer la contraseña no es válido o ya fue utilizado.",
    tokenExpired: "Este enlace para restablecer la contraseña ha caducado. Solicite uno nuevo.",
    googleUnavailable: "El inicio de sesión con Google no está disponible en este momento.",
    invalidEmail: "Ingrese un correo electrónico válido.",
  },
  "fr-CA": {
    genericError: "Une erreur s’est produite. Veuillez réessayer.",
    sending: "Envoi…",
    processing: "Traitement…",
    showPassword: "Afficher le mot de passe",
    hidePassword: "Masquer le mot de passe",
    forgotConfirmation: "Si un compte avec courriel et mot de passe existe pour cette adresse, nous avons envoyé un lien de réinitialisation. Vérifiez votre boîte de réception et votre dossier de pourriels.",
    resetTitle: "Réinitialiser le mot de passe",
    resetSubtitle: "Choisissez un nouveau mot de passe pour votre compte.",
    tokenLabel: "Jeton",
    newPassword: "Nouveau mot de passe",
    confirmNewPassword: "Confirmer le nouveau mot de passe",
    resetSubmit: "Réinitialiser le mot de passe",
    resetSuccess: "Mot de passe réinitialisé. Connectez-vous avec votre nouveau mot de passe.",
    passwordTooShort: "Le mot de passe doit contenir au moins 8 caractères.",
    passwordMismatch: "Les mots de passe ne correspondent pas.",
    changeTitle: "Changez votre mot de passe",
    changeSubtitle: "Pour votre sécurité, définissez un nouveau mot de passe avant de continuer.",
    currentPassword: "Mot de passe actuel",
    saveNewPassword: "Enregistrer le nouveau mot de passe",
    logoutWithoutChanging: "Se déconnecter sans le changer",
    changeSuccess: "Mot de passe modifié.",
    accountCreated: "Compte créé!",
    incorrectCredentials: "Courriel ou mot de passe incorrect.",
    tooManyAttempts: "Trop de tentatives de connexion. Attendez quelques minutes et réessayez.",
    accountDisabled: "Ce compte est désactivé.",
    activeLoginExists: "Ce compte est déjà connecté dans un autre navigateur ou sur un autre appareil. Si cette session n’est plus utilisée, cliquez de nouveau sur Se connecter pour la fermer et continuer ici.",
    authRequired: "Votre session a expiré. Veuillez vous reconnecter.",
    adminOnly: "Accès réservé à l’administrateur.",
    accountExists: "Un compte existe déjà avec ce courriel.",
    privacyRequired: "Vous devez accepter la Politique de confidentialité pour continuer.",
    ageRequired: "Vous devez confirmer avoir atteint l’âge légal de la majorité pour continuer.",
    policyVersion: "La version de la Politique de confidentialité n’a pas été reconnue.",
    policyLanguage: "La langue de la Politique de confidentialité n’est pas prise en charge.",
    countryUnsupported: "Le pays de résidence n’est pas pris en charge pendant la bêta.",
    firstNameRequired: "Entrez votre prénom.",
    googleNoPassword: "Les comptes Google n’utilisent pas de mot de passe de l’application.",
    currentPasswordIncorrect: "Le mot de passe actuel est incorrect.",
    tokenInvalid: "Ce lien de réinitialisation est invalide ou a déjà été utilisé.",
    tokenExpired: "Ce lien de réinitialisation a expiré. Demandez-en un nouveau.",
    googleUnavailable: "La connexion avec Google n’est pas disponible pour le moment.",
    invalidEmail: "Entrez une adresse courriel valide.",
  },
};

export function getAuthMessages(locale) {
  return authMessages[normalizeLocale(locale)] || authMessages.en;
}

const DETAIL_KEYS = new Map([
  ["Não autenticado", "authRequired"],
  ["Sessão inválida", "authRequired"],
  ["Conta desativada", "accountDisabled"],
  ["Account disabled", "accountDisabled"],
  ["Acesso restrito ao administrador", "adminOnly"],
  ["Já existe uma conta com este e-mail", "accountExists"],
  ["Incorrect email or password", "incorrectCredentials"],
  ["Você precisa aceitar a Política de Privacidade para criar uma conta.", "privacyRequired"],
  ["Você precisa aceitar a Política de Privacidade para continuar.", "privacyRequired"],
  ["Você precisa confirmar que atingiu a maioridade legal para criar uma conta.", "ageRequired"],
  ["Você precisa confirmar que atingiu a maioridade legal para continuar.", "ageRequired"],
  ["Versão da Política de Privacidade não reconhecida.", "policyVersion"],
  ["Idioma da Política de Privacidade não suportado.", "policyLanguage"],
  ["País de residência não suportado durante o Beta.", "countryUnsupported"],
  ["Primeiro nome obrigatório", "firstNameRequired"],
  ["Contas Google não usam senha", "googleNoPassword"],
  ["Senha atual incorreta", "currentPasswordIncorrect"],
  ["Token inválido ou já utilizado", "tokenInvalid"],
  ["Token expirado", "tokenExpired"],
  ["Login com Google indisponível no momento", "googleUnavailable"],
]);

export function localizeApiError(err, locale) {
  const t = getAuthMessages(locale);
  const detail = err?.response?.data?.detail;

  if (detail?.code === "active_login_exists") return t.activeLoginExists;

  if (typeof detail === "string") {
    if (detail.startsWith("Too many login attempts.")) return t.tooManyAttempts;
    const key = DETAIL_KEYS.get(detail);
    return key ? t[key] : t.genericError;
  }

  const messages = Array.isArray(detail)
    ? detail.map((item) => item?.msg).filter((msg) => typeof msg === "string")
    : detail?.msg && typeof detail.msg === "string"
      ? [detail.msg]
      : [];

  if (messages.some((msg) => /valid email|email address/i.test(msg))) return t.invalidEmail;
  if (messages.some((msg) => /at least 8|min_length.*8|8 characters/i.test(msg))) return t.passwordTooShort;

  return t.genericError;
}
